import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useState } from "react";
import { QueryObserverResult } from "@tanstack/react-query";

interface CompanyPreferencesModalProps {
  refetchSection: () => Promise<QueryObserverResult<unknown, unknown>>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentPreferences?: {
    industry?: string[];
    company_size?: string[];
    investment_stage?: string[];
  };
}

interface FormValues {
  industry: string[];
  company_size: string[];
  investment_stage: string[];
  hidden_companies?: string;
}

const INDUSTRIES = [
  "No preference",
  "Education",
  "Security",
  "Finance",
  "Analytics",
  "Healthcare",
  "AI/ML",
  "eCommerce",
  "Gaming",
  "Blockchain/Cryptocurrency",
  "Payments",
  "Fintech",
  "Cloud Computing",
  "AR/VR",
  "IoT",
  "Electric Vehicles/Automotive",
  "Dating",
  "Consulting",
  "Productivity Tools",
  "SaaS",
  "Social Network",
  "Travel",
  "Marketplace",
  "Fashion",
  "Recruiting"
];

const COMPANY_SIZES = [
  "No preference",
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501+"
];

const INVESTMENT_STAGES = [
  "No preference",
  "pre-seed",
  "seed",
  "series-a",
  "series-b",
  "series-c",
  "public"
];

const CompanyPreferencesModal = ({ 
  isOpen, 
  onOpenChange, 
  currentPreferences,
  refetchSection
}: CompanyPreferencesModalProps) => {
  const { toast } = useToast();
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(currentPreferences?.industry || []);
  const [selectedSizes, setSelectedSizes] = useState<string[]>(currentPreferences?.company_size || []);
  const [selectedStages, setSelectedStages] = useState<string[]>(currentPreferences?.investment_stage || []);

  const form = useForm<FormValues>({
    defaultValues: {
      industry: currentPreferences?.industry || [],
      company_size: currentPreferences?.company_size || [],
      investment_stage: currentPreferences?.investment_stage || [],
      hidden_companies: "",
    },
  });

  const handleSelectionChange = (value: string, type: 'industry' | 'company_size' | 'investment_stage') => {
    const setterMap = {
      'industry': setSelectedIndustries,
      'company_size': setSelectedSizes,
      'investment_stage': setSelectedStages
    };
    
    const currentValues = {
      'industry': selectedIndustries,
      'company_size': selectedSizes,
      'investment_stage': selectedStages
    }[type];

    let newValues: string[];
    
    if (value === "No preference") {
      newValues = ["No preference"];
    } else {
      if (currentValues.includes(value)) {
        newValues = currentValues.filter(v => v !== value);
      } else {
        newValues = currentValues.includes("No preference") 
          ? [value]
          : [...currentValues, value];
      }
    }
    
    setterMap[type](newValues);
    form.setValue(type, newValues);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('No user found');

      const { error } = await supabase
        .from('developer_profiles')
        .upsert({
          user_id: user.data.user.id,
          industry: data.industry,
          company_size: data.company_size,
          investment_stage: data.investment_stage,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' })
        .eq('user_id', user.data.user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Company preferences updated successfully.",
      });
      refetchSection();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update company preferences. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>Company preferences</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 mb-4">
          The Arc team will view your preferences to help find companies that are a great fit for you.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company size</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {COMPANY_SIZES.map((size) => (
                      <Button
                        key={size}
                        type="button"
                        variant={selectedSizes.includes(size) ? "default" : "outline"}
                        onClick={() => handleSelectionChange(size, 'company_size')}
                        className="h-8"
                      >
                        {size}
                      </Button>
                    ))}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="investment_stage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Investment stage</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {INVESTMENT_STAGES.map((stage) => (
                      <Button
                        key={stage}
                        type="button"
                        variant={selectedStages.includes(stage) ? "default" : "outline"}
                        onClick={() => handleSelectionChange(stage, 'investment_stage')}
                        className="h-8"
                      >
                        {stage}
                      </Button>
                    ))}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industries</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {INDUSTRIES.map((industry) => (
                      <Button
                        key={industry}
                        type="button"
                        variant={selectedIndustries.includes(industry.toLowerCase()) ? "default" : "outline"}
                        onClick={() => handleSelectionChange(industry.toLowerCase(), 'industry')}
                        className="h-8"
                      >
                        {industry}
                      </Button>
                    ))}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hidden_companies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Companies you'd like to be hidden from</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <p className="text-xs text-gray-500 mt-1">
                    Be careful with your keywords. Please type the exact name of the company i.e. "Meta", "Airbnb", "Canonical".
                  </p>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyPreferencesModal;